import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { CompanyResponse } from "@/types/company";

// Fetch all companies for an organization
export function useCompanies(organizationId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['companies', organizationId],
    queryFn: async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiRequest<CompanyResponse[]>("GET", `/organizations/${organizationId}/companies`);
        return response;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch companies';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    enabled: !!organizationId
  });

  return {
    data: query.data || [],
    loading: loading || query.isLoading,
    error: error || (query.error ? String(query.error) : null),
    refetch: query.refetch
  };
}

// Fetch single company details
export function useCompany(organizationId: string, companyId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['company', organizationId, companyId],
    queryFn: async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiRequest<CompanyResponse>("GET", `/organizations/${organizationId}/companies/${companyId}`);
        return response;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch company';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    enabled: !!organizationId && !!companyId
  });

  return {
    data: query.data,
    loading: loading || query.isLoading,
    error: error || (query.error ? String(query.error) : null),
    refetch: query.refetch
  };
}
